import { useLocalStorage } from '@vueuse/core';

import {
  ConfirmSensitiveResponseSchema,
  LoginResponseSchema,
  UpdateEmailResponseSchema,
  UpdateLocaleResponseSchema,
  UpdatePasswordResponseSchema,
  UpdateTimezoneResponseSchema,
  UserSchema,
  type LoginCredentials,
  type LoginResponse,
  type User,
} from '@/schemas/auth';
import type { Result } from '@/types/api-routes';
import { API_ROUTES, apiRequest, ForbiddenError, validatedApiRequest } from '@/types/api-routes';

class AuthService {
  private authToken = useLocalStorage<string | null>('auth_token', null);

  login(credentials: LoginCredentials): Promise<Result<LoginResponse>> {
    return validatedApiRequest(API_ROUTES.auth.login, LoginResponseSchema, credentials);
  }

  getUser(): Promise<Result<User>> {
    return validatedApiRequest(API_ROUTES.auth.user.show, UserSchema);
  }

  logout(): Promise<Result<void>> {
    return apiRequest(API_ROUTES.auth.logout);
  }

  logoutAll(): Promise<Result<void>> {
    return apiRequest(API_ROUTES.auth.logout, { logout_all: true });
  }

  setToken(token: string): void {
    this.authToken.value = token;
  }

  getToken(): string | null {
    return this.authToken.value;
  }

  removeToken(): void {
    this.authToken.value = null;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  updateLocale(locale: string): Promise<Result<{ locale: string }>> {
    return validatedApiRequest(API_ROUTES.auth.user.locale.update, UpdateLocaleResponseSchema, { locale });
  }

  updateTimezone(timezone: string): Promise<Result<{ timezone: string }>> {
    return validatedApiRequest(API_ROUTES.auth.user.timezone.update, UpdateTimezoneResponseSchema, { timezone });
  }

  updatePassword(password: string, passwordConfirmation: string): Promise<Result<void>> {
    return apiRequest(API_ROUTES.auth.user.password.update, UpdatePasswordResponseSchema, {
      password,
      password_confirmation: passwordConfirmation,
    });
  }

  updateEmail(email: string): Promise<Result<{ message: string; newEmail: string }>> {
    return validatedApiRequest(API_ROUTES.auth.user.email.update, UpdateEmailResponseSchema, {
      email,
    });
  }

  confirmSensitive(password: string, otpCode?: string): Promise<Result<{ confirmed_at: string }>> {
    return validatedApiRequest(API_ROUTES.auth.confirm.sensitive, ConfirmSensitiveResponseSchema, {
      password,
      otp_code: otpCode,
    });
  }

  async checkSensitiveConfirmationRequired(): Promise<boolean> {
    const result = await apiRequest(API_ROUTES.auth.check.sensitive);
    if (result.ok) {
      return false;
    }
    if (
      result.error instanceof ForbiddenError &&
      result.error.response &&
      typeof result.error.response === 'object' &&
      'requires_confirmation' in result.error.response &&
      result.error.response.requires_confirmation === true
    ) {
      return true;
    }
    return false;
  }
}

export const authService = new AuthService();

export { type LoginCredentials, type User, type LoginResponse } from '@/schemas/auth';
